import { SquaresFour } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { Button } from "flowbite-react";
import {
  ChartBar,
  Calculator,
  Calendar,
  CurrencyDollar,
  CurrencyInr,
  Megaphone,
  DeviceMobile,
} from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CustomSwitch from "../../../components/CustomSwitch";
import DateRangePickerButton from "../../../components/DateRangePicker/DateRangePickerButton";
import DashboardSkeleton from "../../../components/SkeletonLoaders/DashboardSkeleton";
import StatCard from "../../../components/StatCard";
import { useDateRange } from "../../../contexts/DateRangeContext";
import { useAuth, useDashboardData } from "../../../hooks";
import { IDateRangePreset } from "../../../interfaces";
import { checkPermission } from "../../../lib/utils";
import { PERMISSION_MAPPER } from "../../../shared/constants";
import RecentUserActivity from "../components/RecentUserActivity";
import RevenueAndSpendChart from "../components/RevenueAndSpendChart";
import TopPerformingAppChart from "../components/TopPerformingApps";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, updatePreferences } = useAuth();
  const navigate = useNavigate();
  const dashboardPreference = useMemo(
    () => user?.preferences?.dashboard,
    [user]
  );
  const showWithTax = useMemo(
    () => dashboardPreference?.show_with_tax ?? false,
    [dashboardPreference]
  );
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const { dateRange, selectedPreset, setDateRange, setSelectedPreset } =
    useDateRange();

  const {
    data: dashboardData,
    isLoading,
    refetch,
  } = useDashboardData({
    from: dateRange.startDate,
    to: dateRange.endDate,
  });

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleDateRangeChange = (from: Date, to: Date) => {
    setDateRange(from, to);
    setIsFilterDrawerOpen(false);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const hasAppListPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.APP_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const otherNetworkWiseRevenue = useMemo(() => {
    const networkMap = dashboardData?.metrics?.network_wise_revenue?.networks || {};
    return Object.entries(networkMap)
      .filter(([networkName, networkValue]) => {
        if (networkName === "AdMob Network") return false;
        const numericValue = typeof networkValue === "number" ? networkValue : Number(networkValue);
        return Number.isFinite(numericValue) && numericValue !== 0;
      })
      .map(([networkName, networkValue]) => ({
        label: networkName,
        value: typeof networkValue === "number" ? networkValue : Number(networkValue),
      }));
  }, [dashboardData?.metrics?.network_wise_revenue?.networks]);

  const otherNetworkWiseRevenueTotal = useMemo(() => {
    return otherNetworkWiseRevenue.reduce((sum, item) => sum + (item.value ?? 0), 0);
  }, [otherNetworkWiseRevenue]);

  const admobNetworkRevenue = useMemo(() => {
    const raw = dashboardData?.metrics?.network_wise_revenue?.networks?.["AdMob Network"];
    const num = typeof raw === "number" ? raw : Number(String(raw ?? "0").replace(/,/g, ""));
    return Number.isFinite(num) ? num : 0;
  }, [dashboardData?.metrics?.network_wise_revenue?.networks]);

  const googleIapRevenue = useMemo(() => {
    return (
      Number(
        dashboardData?.metrics?.in_app_purchase
          ?.total_in_app_purchase_proceeds_android
      ) || 0
    );
  }, [dashboardData?.metrics?.in_app_purchase?.total_in_app_purchase_proceeds_android]);

  const appleIapRevenue = useMemo(() => {
    return (
      Number(
        dashboardData?.metrics?.in_app_purchase
          ?.total_in_app_purchase_proceeds_ios
      ) || 0
    );
  }, [dashboardData?.metrics?.in_app_purchase?.total_in_app_purchase_proceeds_ios]);

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-2">
        <div className="flex items-center gap-4 justify-between w-full pl-2">
          <div className="flex items-center gap-2">
            <SquaresFour size={20} className="text-gray-700" weight="duotone" />
            <h1 className="text-md font-bold text-gray-700">
              {t("common.summary")}
            </h1>
          </div>
          {/* Mobile Filter Button */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              color="indigo"
              size="sm"
              className="w-full"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar size={20} />
                <p
                  className={`leading-none ${selectedPreset?.label ? "text-sm" : "text-xs"
                    }`}
                >
                  {selectedPreset?.label ||
                    `${dayjs(dateRange.startDate).format("DD/MM/YY")} - ${dayjs(
                      dateRange.endDate
                    ).format("DD/MM/YY")}`}
                </p>
              </div>
            </Button>
            <CustomSwitch
              checked={showWithTax}
              onChange={(checked) => {
                updatePreferences({
                  dashboard: {
                    ...dashboardPreference,
                    show_with_tax: checked,
                  },
                });
              }}
              label={t("common.withTax")}
            />
          </div>
        </div>
        {/* Desktop Date Range Picker + With Tax Switch */}
        <div className="hidden md:flex items-center gap-4">
          <CustomSwitch
            checked={showWithTax}
            onChange={(checked) => {
              updatePreferences({
                dashboard: {
                  ...dashboardPreference,
                  show_with_tax: checked,
                },
              });
            }}
            label={t("common.withTax")}
          />
          <DateRangePickerButton
            onDateRangeChange={handleDateRangeChange}
            onPresetChange={handlePresetChange}
            initialStartDate={dateRange.startDate}
            initialEndDate={dateRange.endDate}
            initialPreset={selectedPreset?.id}
          />
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity ${isFilterDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setIsFilterDrawerOpen(false)}
      >
        <div
          className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-xl transition-transform transform ${isFilterDrawerOpen ? "translate-y-0" : "translate-y-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t("common.filter")}
              </h3>
              <button
                onClick={() => setIsFilterDrawerOpen(false)}
                className="text-gray-500"
              >
                ×
              </button>
            </div>
            <div className="space-y-4">
              <DateRangePickerButton
                onDateRangeChange={handleDateRangeChange}
                onPresetChange={handlePresetChange}
                initialStartDate={dateRange.startDate}
                initialEndDate={dateRange.endDate}
                initialPreset={selectedPreset?.id}
              />
              <Button
                color="indigo"
                className="w-full mt-4"
                onClick={() => {
                  setIsFilterDrawerOpen(false);
                  refetch();
                }}
              >
                {t("common.apply")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <DashboardSkeleton />
      ) : (
        <>
          {dashboardData ? (
            <>
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <StatCard
                    title={t("dashboard.summary.apps")}
                    value={dashboardData.metrics.total_apps}
                    icon={<DeviceMobile size={24} weight="duotone" />}
                    onClick={() => handleNavigation("/apps")}
                  />
                  <StatCard
                    title={t("common.campaigns")}
                    value={dashboardData.metrics.total_campaigns}
                    isInAppPurchase={true}
                    totalEstimatedRevenue={[
                      {
                        value: dashboardData.metrics.total_active_campaigns,
                        label: t("common.enabled")
                      },
                      {
                        value: dashboardData.metrics.total_paused_campaigns,
                        label: t("common.paused")
                      }
                    ]}
                    icon={<Megaphone size={24} weight="duotone" />}
                  />
                   <StatCard
                    title={t("dashboard.summary.estimatedRevenue")}
                    value={
                      parseFloat(
                        dashboardData?.metrics.total_revenue?.replace(
                          /,/g,
                          ""
                        ) ?? "0"
                      )
                    }
                    isCurrency={true}
                    icon={<Calculator size={24} weight="duotone" />}
                    isInAppPurchase={true}
                    totalEstimatedRevenue={[
                      {
                        value: admobNetworkRevenue + otherNetworkWiseRevenueTotal,
                        label: t("dashboard.summary.networkRevenue"),
                        valueClassName: "text-[#02a749] font-bold", // Applied color to the value
                        tooltipItems: [
                          {
                            label: "Network",
                            value: admobNetworkRevenue,
                            labelClassName: "text-emerald-400 font-semibold",
                          },
                          {
                            label: "Mediation",
                            value: otherNetworkWiseRevenueTotal,
                            children: otherNetworkWiseRevenue,
                          },
                        ],
                      },
                      {
                        value: googleIapRevenue,
                        label: t("dashboard.summary.googleIap"),
                      },
                      {
                        value: appleIapRevenue,
                        label: t("dashboard.summary.appleIap"),
                      },
                    ]}
                  />
                   <StatCard
                    title={t("dashboard.summary.totalNet")}
                    value={parseFloat(
                      dashboardData.metrics.total_usd_net_without_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    withTaxValue={parseFloat(
                      dashboardData.metrics.total_usd_net_with_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    showWithTax={showWithTax}
                    isCurrency={true}
                    icon={<ChartBar size={24} weight="duotone" />}
                  />
                  <StatCard
                    title={t("dashboard.summary.spend")}
                    value={parseFloat(
                      dashboardData.metrics.total_spend_cost_usd_without_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    withTaxValue={parseFloat(
                      dashboardData.metrics.total_spend_cost_usd_with_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    showWithTax={showWithTax}
                    isCurrency={true}
                    icon={<CurrencyDollar size={24} weight="duotone" />}
                  />
                  <StatCard
                    title={t("dashboard.summary.spend")}
                    value={parseFloat(
                      dashboardData.metrics.total_spend_cost_inr_without_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    withTaxValue={parseFloat(
                      dashboardData.metrics.total_spend_cost_inr_with_tax?.replace(
                        /,/g,
                        ""
                      ) || "0"
                    )}
                    showWithTax={showWithTax}
                    currency="INR"
                    isCurrency={true}
                    icon={<CurrencyInr size={24} weight="duotone" />}
                  />
                  {/* <StatCard
                    title={t("dashboard.metrics.campaign.title")}
                    value={
                      dashboardData?.metrics?.campaign_metrics
                        ?.total_conversions ?? "0"
                    }
                    icon={<Megaphone size={24} weight="duotone" />}
                    subStats={[
                      {
                        label: t("dashboard.metrics.campaign.clicks"),
                        value:
                          dashboardData?.metrics?.campaign_metrics
                            ?.total_clicks ?? "0",
                      },
                      {
                        label: t("dashboard.metrics.campaign.impressions"),
                        value:
                          dashboardData?.metrics?.campaign_metrics
                            ?.total_impressions ?? "0",
                      },
                      {
                        label: t("dashboard.metrics.campaign.avgCtr"),
                        value:
                          dashboardData?.metrics?.campaign_metrics
                            ?.average_ctr ?? "0%",
                      },
                      {
                        label: t("dashboard.metrics.campaign.costPerConv"),
                        value:
                          dashboardData?.metrics?.campaign_metrics
                            ?.cost_per_conversion ?? "0",
                      },
                    ]}
                  />
                  <StatCard
                    title={t("dashboard.metrics.admob.title")}
                    value={
                      dashboardData?.metrics?.admob_metrics
                        ?.total_impressions ?? "0"
                    }
                    icon={<ChartBar size={24} weight="duotone" />}
                    subStats={[
                      {
                        label: t("dashboard.metrics.admob.adRequests"),
                        value:
                          dashboardData?.metrics?.admob_metrics
                            ?.total_ad_requests ?? "0",
                      },
                      {
                        label: t("dashboard.metrics.admob.matchRate"),
                        value:
                          dashboardData?.metrics?.admob_metrics
                            ?.match_rate ?? "0%",
                      },
                      {
                        label: t("dashboard.metrics.admob.ecpm"),
                        value:
                          dashboardData?.metrics?.admob_metrics?.ecpm ?? "$0",
                      },
                    ]}
                  /> */}
                </div>
                <RevenueAndSpendChart
                  data={dashboardData?.chart_data}
                  showWithTax={showWithTax}
                />
              </div>
            </>
          ) : null}
        </>
      )}
      {hasAppListPermission && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {hasAppListPermission && (
            <TopPerformingAppChart showWithTax={showWithTax} />
          )}
          <RecentUserActivity />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
